/**
 * Region validation & inspector suggestion — Phase 5 port.
 *
 * Replaces the hardcoded REGIONS map from RebuildLocationSort with live reads
 * from `suburb_cache` (Phase 4). Match order:
 *
 *   1. Exact suburb name (preferred — already-known mapping from the sheet).
 *   2. Postcode (broader — first row wins).
 *   3. Haversine within 75km of any active inspector's region centroid
 *      (computed from the average lat/lng of all suburb_cache rows for that zone).
 *   4. No match.
 *
 * Inspector suggestion ranks active inspectors by drive distance to the
 * deal address, weighted by current schedule. The actual schedule weight is
 * a Phase 6 concern — for now we just pick the closest active inspector
 * whose region matches the resolved zone.
 */

import { getSupabase } from '../lib/supabase';
import { getSuburbByName, getSuburbByPostcode, getVerdictForSuburb, type SuburbRow } from '../lib/suburbs';
import { invalidateSuburbCache, loadSuburbCache } from '../lib/suburbCache';

/** Earth radius in km — Haversine constant. */
const EARTH_RADIUS_KM = 6371;
const DEFAULT_SERVICE_RADIUS_KM = 75;

export interface RegionMatch {
  zone: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  verdict: { label: string; meaning: string; action: string } | null;
  matchType: 'suburb-exact' | 'postcode' | 'haversine' | 'none';
  distanceKm: number | null;
}

export interface InspectorSuggestion {
  inspectorId: string;
  inspectorName: string;
  regionCode: string | null;
  reason: string;
  distanceKm: number | null;
}

interface InspectorRow {
  id: string;
  name: string;
  region_code: string | null;
  is_active: boolean;
  config: Record<string, unknown> | null;
}

interface ZoneCentroid {
  zone: string;
  lat: number;
  lng: number;
}

/** Haversine distance between two points (km). Pure maths — no network. */
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_KM * c;
}

/**
 * Parse a free-form address string into `{ suburb, state, postcode }`.
 *
 * Australian addresses follow `[unit/]street, suburb STATE POSTCODE` strongly
 * enough that a regex gets us 90% there. Falls back to last comma-separated
 * chunk when the full pattern doesn't match.
 */
export function parseAusAddress(address: string): {
  suburb: string | null;
  state: string | null;
  postcode: string | null;
} {
  if (!address) return { suburb: null, state: null, postcode: null };

  // Match "<suburb> <STATE> <4-digit postcode>" at end of string.
  const tail = address.match(/([A-Za-z][A-Za-z\s'-]+?)\s+(NSW|QLD|VIC|TAS|SA|WA|ACT|NT)\s+(\d{4})\s*(?:,?\s*Australia)?$/i);
  if (tail) {
    return {
      suburb: tail[1].trim(),
      state: tail[2].toUpperCase(),
      postcode: tail[3],
    };
  }

  // Fallback: last 4-digit number is the postcode, last word before it the state.
  const pcMatch = address.match(/\b(\d{4})\b/);
  const stMatch = address.match(/\b(NSW|QLD|VIC|TAS|SA|WA|ACT|NT)\b/i);
  // Suburb = the comma-separated chunk just before state/postcode, best-effort.
  const segments = address.split(',').map((s) => s.trim()).filter(Boolean);
  const suburb = segments.length >= 2 ? segments[segments.length - 2] : null;

  return {
    suburb: suburb ?? null,
    state: stMatch ? stMatch[1].toUpperCase() : null,
    postcode: pcMatch ? pcMatch[1] : null,
  };
}

/**
 * Compute zone centroids by averaging the lat/lng of every suburb_cache row
 * for each zone. Cheap to run once and memoise — there are typically <20
 * zones and the table is indexed. If a row is missing lat/lng (older Sheet
 * imports often did), it's skipped.
 */
let _zoneCentroidsCache: { ts: number; data: ZoneCentroid[] } | null = null;
const CENTROID_TTL_MS = 5 * 60_000;

async function getZoneCentroids(): Promise<ZoneCentroid[]> {
  if (_zoneCentroidsCache && Date.now() - _zoneCentroidsCache.ts < CENTROID_TTL_MS) {
    return _zoneCentroidsCache.data;
  }

  // Preferred path: piggy-back on the worker-hydrated suburb cache so we
  // don't double-fetch the table or repeat the centroid math on the main
  // thread. `loadSuburbCache` is idempotent — first caller pays, the rest
  // await the same promise.
  const hydrated = await loadSuburbCache();
  if (hydrated) {
    _zoneCentroidsCache = { ts: Date.now(), data: hydrated.centroids };
    return hydrated.centroids;
  }

  // Fallback: hydration unavailable (worker errored before main-thread
  // fallback ran, or rows table missing). Compute inline against Supabase.
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('v9_suburb_cache')
    .select('zone, raw_row')
    .not('zone', 'is', null);

  if (error || !data) {
    if (error) console.warn('[regionValidation] zone centroids fetch failed:', error.message);
    return [];
  }

  const buckets = new Map<string, { latSum: number; lngSum: number; n: number }>();
  for (const row of data as { zone: string; raw_row: Record<string, unknown> | null }[]) {
    const lat = extractNumber(row.raw_row, ['lat', 'latitude', 'centroid_lat', 'Lat', 'Latitude']);
    const lng = extractNumber(row.raw_row, ['lng', 'longitude', 'centroid_lng', 'Lng', 'Longitude', 'Long']);
    if (lat == null || lng == null) continue;
    const cur = buckets.get(row.zone) ?? { latSum: 0, lngSum: 0, n: 0 };
    cur.latSum += lat;
    cur.lngSum += lng;
    cur.n += 1;
    buckets.set(row.zone, cur);
  }

  const out: ZoneCentroid[] = [];
  for (const [zone, agg] of buckets.entries()) {
    if (agg.n === 0) continue;
    out.push({ zone, lat: agg.latSum / agg.n, lng: agg.lngSum / agg.n });
  }
  _zoneCentroidsCache = { ts: Date.now(), data: out };
  return out;
}

function extractNumber(obj: Record<string, unknown> | null | undefined, keys: string[]): number | null {
  if (!obj) return null;
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const n = parseFloat(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

/** Force a refresh of the zone-centroid cache — call after a suburb_cache resync. */
export function clearRegionValidationCache(): void {
  _zoneCentroidsCache = null;
  // Also blow the hydrated suburb cache so the next call re-fetches the
  // freshly-resynced rows.
  invalidateSuburbCache();
}

/**
 * Resolve an address (and optional coordinates) to a zone + verdict.
 *
 * Pass `lat`/`lng` when you have them (from Google Places autocomplete) so
 * Haversine matching can run if suburb/postcode lookups miss. Without them,
 * only the suburb-exact / postcode paths can hit.
 */
export async function findRegionForAddress(
  address: string,
  lat?: number,
  lng?: number,
): Promise<RegionMatch> {
  const parsed = parseAusAddress(address);

  // 1. Exact suburb match.
  if (parsed.suburb) {
    const row = await getSuburbByName(parsed.suburb, parsed.state ?? undefined);
    if (row) {
      return rowToMatch(row, 'suburb-exact', null);
    }
  }

  // 2. Postcode match.
  if (parsed.postcode) {
    const rows = await getSuburbByPostcode(parsed.postcode);
    if (rows.length > 0) {
      // Prefer a row that also matches the parsed state when present.
      const stateMatch = parsed.state ? rows.find((r) => r.state === parsed.state) : undefined;
      const best = stateMatch ?? rows[0];
      return rowToMatch(best, 'postcode', null);
    }
  }

  // 3. Haversine — only possible with coordinates.
  if (typeof lat === 'number' && typeof lng === 'number') {
    const centroids = await getZoneCentroids();
    let best: { zone: string; dist: number } | null = null;
    for (const c of centroids) {
      const d = calculateDistance(lat, lng, c.lat, c.lng);
      if (!best || d < best.dist) best = { zone: c.zone, dist: d };
    }
    if (best && best.dist <= DEFAULT_SERVICE_RADIUS_KM) {
      // We have a zone but no specific suburb row — verdict resolves async
      // from any suburb in that zone (best-effort), otherwise null.
      const verdict = await firstVerdictInZone(best.zone);
      return {
        zone: best.zone,
        suburb: null,
        state: parsed.state,
        postcode: parsed.postcode,
        verdict,
        matchType: 'haversine',
        distanceKm: Math.round(best.dist * 10) / 10,
      };
    }
    if (best) {
      // Out-of-area but closest known zone — still useful context.
      return {
        zone: null,
        suburb: null,
        state: parsed.state,
        postcode: parsed.postcode,
        verdict: null,
        matchType: 'none',
        distanceKm: Math.round(best.dist * 10) / 10,
      };
    }
  }

  return {
    zone: null,
    suburb: parsed.suburb,
    state: parsed.state,
    postcode: parsed.postcode,
    verdict: null,
    matchType: 'none',
    distanceKm: null,
  };
}

function rowToMatch(
  row: SuburbRow,
  matchType: 'suburb-exact' | 'postcode',
  distanceKm: number | null,
): RegionMatch {
  const verdict =
    row.verdictLabel != null
      ? {
          label: row.verdictLabel,
          meaning: '',
          action: '',
        }
      : null;
  return {
    zone: row.zone,
    suburb: row.suburb,
    state: row.state,
    postcode: row.postcode,
    verdict,
    matchType,
    distanceKm,
  };
}

async function firstVerdictInZone(zone: string): Promise<{ label: string; meaning: string; action: string } | null> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('v9_suburb_cache')
    .select('suburb, state, verdict_label')
    .eq('zone', zone)
    .not('verdict_label', 'is', null)
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  return getVerdictForSuburb(data.suburb, data.state ?? undefined);
}

/**
 * Suggest the best inspector for a deal address.
 *
 * Strategy: resolve the address to a zone, then pick the active inspector
 * whose `region_code` matches that zone. If none match (or the address is
 * out-of-area), fall back to the closest active inspector by Haversine
 * distance to their region centroid.
 */
export async function suggestInspectorForDeal(
  tenantId: string,
  dealAddress: string,
  dealLat?: number,
  dealLng?: number,
): Promise<InspectorSuggestion | null> {
  const supabase = getSupabase();
  const { data: inspectorRows, error } = await supabase
    .from('v9_inspectors')
    .select('id, name, region_code, is_active, config')
    .eq('tenant_id', tenantId)
    .eq('is_active', true);

  if (error || !inspectorRows || inspectorRows.length === 0) {
    if (error) console.warn('[regionValidation] suggestInspector inspectors fetch failed:', error.message);
    return null;
  }

  const inspectors = inspectorRows as InspectorRow[];
  const region = await findRegionForAddress(dealAddress, dealLat, dealLng);

  // Zone-match path.
  if (region.zone) {
    const inZone = inspectors.filter((i) => i.region_code === region.zone);
    if (inZone.length > 0) {
      const pick = inZone[0]; // First active inspector in zone (schedule-aware ranking lands in Phase 6).
      return {
        inspectorId: pick.id,
        inspectorName: pick.name,
        regionCode: pick.region_code,
        reason: `In zone ${region.zone}${region.suburb ? ` (${region.suburb})` : ''}`,
        distanceKm: region.distanceKm,
      };
    }
  }

  // Haversine fallback — need deal coordinates AND inspector coordinates
  // (pulled from their config blob). Skip silently if either is missing.
  if (typeof dealLat !== 'number' || typeof dealLng !== 'number') return null;

  const centroids = await getZoneCentroids();
  const centroidByZone = new Map(centroids.map((c) => [c.zone, c]));

  let best: { inspector: InspectorRow; dist: number } | null = null;
  for (const ins of inspectors) {
    if (!ins.region_code) continue;
    const c = centroidByZone.get(ins.region_code);
    if (!c) continue;
    const d = calculateDistance(dealLat, dealLng, c.lat, c.lng);
    if (!best || d < best.dist) best = { inspector: ins, dist: d };
  }

  if (!best) return null;
  return {
    inspectorId: best.inspector.id,
    inspectorName: best.inspector.name,
    regionCode: best.inspector.region_code,
    reason: `Closest inspector — ${best.dist.toFixed(1)}km from their region centroid`,
    distanceKm: Math.round(best.dist * 10) / 10,
  };
}

/**
 * Verdict bucket — collapses the open-ended verdict label set into the three
 * coloured buckets the dashboard reasons about: approved (book-worthy area),
 * review (needs a look), outside (out-of-scope / unknown).
 *
 * Single source of truth: DealSidebar's verdict filter chips, the map verdict
 * colouring, and lead classification (lead → lead_to_book) all key off this so
 * an "approved" verdict means the same thing everywhere.
 */
export type VerdictBucket = 'approved' | 'review' | 'outside';

export function verdictBucket(label: string | null | undefined): VerdictBucket {
  if (!label) return 'outside';
  const l = label.toLowerCase();
  if (l.includes('hot') || l.includes('warm') || l.includes('approved') || l.includes('cool') || l.includes('green')) {
    return 'approved';
  }
  if (l.includes('review')) return 'review';
  return 'outside';
}

/** Verdict colour mapping used by both map pins and sidebar chips. */
export function verdictColour(label: string | null | undefined): string {
  if (!label) return '#6b7280'; // neutral-500
  const l = label.toLowerCase();
  if (l.includes('hot')) return '#dc2626'; // red-600 — high-density, top priority
  if (l.includes('warm')) return '#f59e0b'; // amber-500 — book soon
  if (l.includes('cool')) return '#10b981'; // emerald-500 — approved area
  if (l.includes('cold')) return '#6b7280'; // neutral-500 — out-of-scope
  if (l.includes('approved') || l.includes('green')) return '#10b981';
  if (l.includes('review')) return '#f59e0b';
  if (l.includes('outside') || l.includes('red')) return '#dc2626';
  return '#6b7280';
}
